import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Platform, ToastController } from '@ionic/angular';
import { ApiService } from 'src/app/services/api.service';
import { EMAIL_PATTERN, MOBILE_PATTERN } from 'src/app/types/form';
import { SocialResponseInterface, UserInterface } from 'src/app/types/user';
import { environment } from 'src/environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { GlobalDataService } from 'src/app/services/global-data.service';

interface optinItem {
  id: string;
  name: string;
  optIn: boolean;
  dirty?: boolean;
}

@Component({
  selector: 'app-profile',
  templateUrl: './profile.page.html',
  styleUrls: ['./profile.page.scss'],
})
export class ProfilePage implements OnInit {
  ssoProfile;
  user: SocialResponseInterface;
  isBrowser: boolean = false;
  cordova: boolean = false;
  checkSSO: boolean = true;
  ageValid: boolean;
  mobileMinLengthError: boolean = false;
  dobDayValues = Array.from({ length: 31 }, (v, k) =>
    (k + 1).toString().length < 2 ? '0' + (k + 1) : k + 1
  );
  dobMonthValues = Array.from({ length: 12 }, (v, k) =>
    (k + 1).toString().length < 2 ? '0' + (k + 1) : k + 1
  );
  dobYearValues = Array.from(
    { length: new Date().getFullYear() - 13 - 1930 + 1 },
    (_, i) => 1930 + i
  ).reverse();
  transformCheckbox: boolean = false;

  /**
   * Loading state variable to toggle UI updates while request loads
   *
   * @type {boolean}
   * @memberof ProfilePage
   */
  loading: boolean = false;

  /**
   * Submitted state variable to toggle
   *
   * @type {boolean}
   * @memberof ProfilePage
   */
  submitted: boolean = false;

  /**
   * Error received from API on submit
   *
   * @type {boolean}
   * @memberof ProfilePage
   */
  errorResponse: string;

  /**
   * Success message after API submit
   *
   * @type {boolean}
   * @memberof ProfilePage
   */
  successResponse: string;

  /**
   * Reset Password form data model
   *
   * @type {boolean}
   * @memberof ProfilePage
   */
  profileForm: FormGroup;

  optinsList: optinItem[];

  @ViewChild('formTop') formTop: ElementRef;

  /**
   * Creates an instance of PasswordPage
   * @param {ApiService} API The ApiService is included to allow network requests from this component
   * @param {Router} router The module that provides navigation and URL manipulation capabilities
   * @memberof ProfilePage
   */
  constructor(
    private platform: Platform,
    private API: ApiService,
    private globalData: GlobalDataService,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    public toastController: ToastController,
    @Inject(PLATFORM_ID) platformId: string
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
    });
  }

  createForm() {
    this.profileForm = this.formBuilder.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      emailAdd: ['', [Validators.required, Validators.pattern(EMAIL_PATTERN)]],
      mobile: ['', [Validators.required, Validators.pattern(MOBILE_PATTERN)]],
      dobDay: ['', [Validators.required]],
      dobMonth: ['', [Validators.required]],
      dobYear: ['', [Validators.required]],
      gender: [null],
    });
    this.profileForm.valueChanges.subscribe((data) => {
      this.validateAge(data);
    });
  }

  validateAge(data) {
    if (data.dobDay !== '' && data.dobMonth !== '' && data.dobYear !== '') {
      const currentDate = new Date();
      const birthDate = new Date(data.dobMonth + '/' + data.dobDay + '/' + data.dobYear);
      const eighteenYearsAgo = currentDate.setFullYear(currentDate.getFullYear() - 18);

      if (birthDate.getTime() <= new Date(eighteenYearsAgo).getTime()) {
        this.ageValid = true;
      } else {
        this.ageValid = false;
      }
    }
  }

  toggleOptin(optin) {
    if (!this.transformCheckbox) {
      this.transformCheckbox = true;
      this.optinsList.map((item) => {
        if (item.id === optin.id) {
          item.optIn = !item.optIn;
          item.dirty = true;
        }

        return item;
      });
      setTimeout(() => {
        this.transformCheckbox = false;
      }, 100);
    }
  }

  onSubmit(formData) {
    this.loading = true;
    this.submitted = true;
    this.successResponse = null;

    if (this.profileForm.valid && this.ageValid) {
      this.errorResponse = null;

      if (
        formData.mobile &&
        formData.mobile.length > 0 &&
        formData.mobile.length < 10
      ) {
        this.mobileMinLengthError = true;
        this.loading = false;
        this.formTop.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
        return;
      } else {
        this.mobileMinLengthError = false;
      }

      const formDataValues = {
        email: formData.emailAdd,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dob: formData.dobYear
          ? formData.dobYear + '-' + formData.dobMonth + '-' + formData.dobDay
          : undefined,
        gender: formData.gender ?? undefined,
        mobile: formData.mobile,
        source: environment.mall_id,
        platform: this.platform.is('android')
          ? 'android'
          : this.platform.is('ios')
          ? 'ios'
          : this.platform.is('mobile') || this.platform.is('mobileweb')
          ? 'mobile'
          : 'desktop',
      };

      this.API.PUT('public/shoppers', formDataValues).subscribe({
        next: () => {
          this.loading = false;
          this.submitted = false;

          const user: UserInterface = {
            ...formDataValues,
            id: this.user.id,
            terms: true,
            optIns: this.optinsList,
          };

          this.authService.setUser(user);

          this.successResponse = 'Your profile has been updated';
          this.formTop.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });
        },
        error: (response) => {
          this.loading = false;

          if (response.email?.message) {
            this.errorResponse = response.email?.message;
          } else if (response.firstName?.message) {
            this.errorResponse = response.firstName?.message;
          } else if (response.lastName?.message) {
            this.errorResponse = response.lastName?.message;
          } else if (response.dob?.message) {
            this.errorResponse = response.dob?.message;
          } else if (response.gender?.message) {
            this.errorResponse = response.gender?.message;
          } else if (response.mobile?.message) {
            this.errorResponse = response.mobile?.message;
          } else if (
            response.error?.message &&
            typeof response.error.message === 'string'
          ) {
            this.errorResponse = response.error.message;
          } else if (response && typeof response === 'string') {
            this.errorResponse = response;
          } else {
            this.errorResponse = 'An error occured. Please try again';
          }

          this.formTop.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });
        },
      });

      for (let index = 0; index < this.optinsList.length; index++) {
        if (this.optinsList[index].dirty) {
          let boolString = this.optinsList[index].optIn.toString();
          boolString = boolString.charAt(0).toUpperCase() + boolString.slice(1);

          this.API.PUT('shoppers/tracking/log', {
            action: 'marketingOptin',
            title: boolString,
            url:
              typeof window !== 'undefined'
                ? window.location.href
                : this.platform.is('android')
                ? 'android'
                : this.platform.is('ios'),
            mallId: this.optinsList[index].id,
          }).subscribe();
        }
      }
    } else {
      this.loading = false;
      this.errorResponse =
        'Please ensure all required fields have been completed';
      this.formTop.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }

  setProfileData(profile) {
    if (profile.firstName)
      this.profileForm.controls.firstName.setValue(profile.firstName);

    if (profile.lastName)
      this.profileForm.controls.lastName.setValue(profile.lastName);

    if (profile.email)
      this.profileForm.controls.emailAdd.setValue(profile.email);

    if (profile.mobile)
      this.profileForm.controls.mobile.setValue(profile.mobile);

    if (profile.gender)
      this.profileForm.controls.gender.setValue(profile.gender);

    if (profile.dob) {
      const dob = profile.dob.split('-');
      this.profileForm.controls.dobYear.setValue(dob[0]);
      this.profileForm.controls.dobMonth.setValue(dob[1]);
      this.profileForm.controls.dobDay.setValue(dob[2]);
    }

    if (profile.optIns)
      this.optinsList = profile.optIns.sort((a, b) =>
        a.name > b.name ? 1 : b.name > a.name ? -1 : 0
      );
  }

  ngOnInit() {
    this.route.data.subscribe((response) => {
      this.createForm();

      this.user = response.data;
      this.setProfileData(this.user);
    });
  }
}
